// @flow

import _ from 'lodash';
import memoize from 'memoize-weak';

import { Colors } from '@zapier/theme';
import {
  type ServiceActionType,
  isLiveDataRequired,
  utils as serviceUtils,
  ServiceActionTypes,
  actionMeta,
  actionNoun,
  actionName,
} from '@zapier/services';
import {
  serviceUtils as commonServiceUtils,
  decodeHTMLEntities,
} from '@zapier/common-utils';

import fuzzyscore from 'app/common/fuzzyscore';
import fuzzysearch from 'app/common/fuzzysearch';
import getVisibleServices from 'app/service/utils/getVisibleServices';
import isServiceHidden from 'app/service/utils/isServiceHidden';
import { NAME, nodeTypes } from 'app/service/constants';
import { compareVersions, exists, get } from 'app/common/CommonUtils';

import type {
  AuthsMap,
  Service,
  ServiceAction,
  ServiceField,
  ServicesMap,
} from 'app/service/types';

const { compareNatural } = serviceUtils;
const { getVersion, hasVersion, stripVersion } = commonServiceUtils;

export * from '@zapier/services/src/utils/sortByPopularityRank'; // TODO: unnecessary?

const actionTypes = [
  nodeTypes.HAS_SEARCHES,
  nodeTypes.HAS_WRITES,
  nodeTypes.HAS_FILTERS,
];

const typeKeys = {
  read: [nodeTypes.HAS_READS],
  search: actionTypes,
  search_or_write: actionTypes,
  write: actionTypes,
  filter: actionTypes,
  paths_group: actionTypes,
};

const PATH_API = 'BranchingAPI';
const isPathApi = (api?: string) => api === PATH_API;

const isPathTrue = (path, service) => Boolean(_.get(service, path, null));
const isAnyPathTrue = (paths, service) =>
  paths.some(path => isPathTrue(path, service));

// The multiple keys here represent legacy data structure and the updated
// GraphQL equivalent
export const isBeta = _.partial(isAnyPathTrue, ['beta', 'isBeta']);
export const isBuiltIn = (service: Service) =>
  service.isBuiltIn || _.get(service, 'builtIn');
export const isDeprecated = _.partial(isAnyPathTrue, [
  'deprecated',
  'isDeprecated',
]);
export const isFeatured = (service: Service) =>
  service.isFeatured || _.get(service, 'featured');
export const isHidden = isServiceHidden;
export const isPremium = _.partial(isAnyPathTrue, ['premium', 'isPremium']);
export const isInvite = (service: Service) =>
  service.isInvite || _.get(service, 'invite_app');
export const isPublic = (service: Service) =>
  service.isPublic || _.get(service, 'public');
export const isOauth = (service: Service) => {
  const authType = _.get(service, 'authType', null);
  const oauthService = _.get(service, ['meta', 'oauth'], false);
  return authType === 'oauth' || oauthService;
};
export const hasUpfrontFields = (service: Service) => {
  return (
    Boolean(service.hasUpfrontFields) || !_.isEmpty(service.upfront_fields)
  );
};
export const excludePathsApi = (services: Service[]) =>
  _.filter(services, ({ key }) => !isPathApi(key));

const actionHelpTextHTML = (action: ServiceAction) => {
  // This flag is put here by the reducer. So depending on it is a bit of a
  // smell. But it's probably non-trivial to refactor that out.
  if (action.hasCreate) {
    return `<p>${action.help_text} Optionally, create one if none are found.</p>`;
  }

  return action.help_text_html;
};

export const actionHelpText = (action: ServiceAction) => {
  return decodeHTMLEntities(actionHelpTextHTML(action).replace(/<[^>]*>/g, ''));
};

const nodeTypeToDelegateMap: { [ServiceActionType]: string } = {
  [ServiceActionTypes.SEARCH_OR_WRITE]: 'key',
  [ServiceActionTypes.WRITE]: 'write_delegate',
  [ServiceActionTypes.SEARCH]: 'search_delegate',
};

const getSearchOrWriteMeta = (service, currentAction, currentTypeOf) => {
  const lookupKey = nodeTypeToDelegateMap[currentTypeOf];

  return _.find(service.search_or_write, {
    [lookupKey]: currentAction,
  });
};

export type ServiceRecommendation = {
  slug: string,
  score: number,
};
const getRecommendationScore = (
  service: Service,
  recommendationScoresBySlug: { [slug: string]: ?ServiceRecommendation }
) => {
  const slug = _.get(service, 'slug');
  if (!slug) {
    return 0;
  }

  return _.get(recommendationScoresBySlug, [slug, 'score'], 0);
};

export const sortByRecommendations = memoize(
  (
    services: Array<Service>,
    recommendationScores: Array<ServiceRecommendation>
  ) => {
    const recommendationScoresBySlug =
      _.keyBy(recommendationScores, 'slug') || {};

    return services.sort((serviceA, serviceB) => {
      const scoreServiceA = getRecommendationScore(
        serviceA,
        recommendationScoresBySlug
      );
      const scoreServiceB = getRecommendationScore(
        serviceB,
        recommendationScoresBySlug
      );

      const compareScore = scoreServiceB - scoreServiceA;
      if (compareScore) {
        return compareScore;
      }

      return compareNatural(
        _.toString(serviceA.name),
        _.toString(serviceB.name)
      );
    });
  }
);

export const rawUpfronts = (
  upfrontFields: Array<ServiceField>
): Array<ServiceField> => {
  if (!upfrontFields) {
    return [];
  }

  return upfrontFields.filter(field => {
    return !(exists(field.important) && field.important === false);
  });
};

export const needsAuth = (service: Service) => {
  const upfrontFields = _.get(service, 'upfront_fields', []);
  const filteredUpfrontFields = rawUpfronts(upfrontFields);

  // `service.authType` is provided by the GraphQL response, if it does not
  // return `null`, the service requires some sort of auth
  if (service.authType) {
    return true;
  }

  if (filteredUpfrontFields.length > 0) {
    return true;
  }

  if (_.get(service, ['meta', 'oauth'], false)) {
    return true;
  }

  return false;
};

export function getAvailableServices(
  services: ServicesMap | Array<Service>,
  {
    nodeType,
    onlyPublic = false,
    showAllNodeTypes = false,
  }: {
    nodeType: ServiceActionType,
    onlyPublic: boolean,
    showAllNodeTypes?: boolean,
  }
) {
  const maybePublicCheck: (service: Service) => ?boolean = onlyPublic
    ? isPublic
    : _.stubTrue;

  const nodeTypeKeys = typeKeys[nodeType] || ['unknown'];
  return _.filter(services, service => {
    if (showAllNodeTypes) {
      return !service.hidden && maybePublicCheck(service);
    }
    return (
      !service.hidden &&
      // $FlowFixMe
      _.some(nodeTypeKeys, k => service[k]) &&
      maybePublicCheck(service)
    );
  });
}

type ObjectWithSelectedApi = {
  selected_api: string,
};
type ObjectWithNodes = {
  nodes: { [id: string]: ObjectWithSelectedApi },
};

export function connectedServices(
  services: ServicesMap | Service[],
  auths: AuthsMap,
  flow: ObjectWithNodes
): Service[] {
  const authApis = _.fromPairs(
    _.values(auths).map(auth => [auth.selected_api, true])
  );
  const flowApis = _.fromPairs(
    _.values(flow.nodes).map(node => [node.selected_api, true])
  );
  const apis = _.extend(flowApis, authApis);
  const servicesArray = Array.isArray(services) ? services : _.values(services);
  return servicesArray.filter(service => apis[service.key]);
}

export function getYourServices(
  selectedApi: string,
  auths: AuthsMap,
  flow: ObjectWithNodes,
  services: Service[],
  transform: (Service[]) => Service[]
): Service[] {
  const servicesArray = connectedServices(services, auths, flow);

  // move the current node's selected_api to first position for highlighting current
  const fromIndex = _.indexOf(
    _.map(servicesArray, service => service.key),
    selectedApi
  );
  if (fromIndex !== -1) {
    servicesArray.splice(0, 0, servicesArray.splice(fromIndex, 1)[0]);
  }

  return transform(servicesArray);
}

export function getServicesByKey(
  partialServices: Service[],
  services: Service[]
): Service[] {
  const partialServicesIds = partialServices.map(service => service.key);
  return services.filter(service => partialServicesIds.includes(service.key));
}

export function getServiceByKey(
  services: ServicesMap | Service[],
  serviceKey: ?string
) {
  return _.find(services, ({ key }) => key === serviceKey);
}

const ServiceUtils = {
  name(service: Service = {}) {
    if (service.name) {
      return service.name;
    }

    if (service.title) {
      return service.title;
    }

    if (exists(service.meta, ['human_readable_key'])) {
      return get(service.meta, ['human_readable_key']);
    }

    return service.key || 'Unnamed service';
  },

  nameFromApi(api: string, services: ServicesMap | {}) {
    return ServiceUtils.name(services[api]);
  },

  requiresLiveData(service: Service) {
    // For now, we use app source in ZT only for Formatter and Code
    return isLiveDataRequired(service.key);
  },

  needsAuth,

  rawUpfronts,

  actionName,

  actionNoun,

  allVisibleLabelsOfType(
    type: 'reads' | 'searches' | 'writes',
    service: Service
  ) {
    return _.chain(service)
      .get(['actions', type])
      .filter(action => !_.get(action, 'hide'))
      .map(action => _.get(action, 'label'))
      .filter(Boolean)
      .uniq()
      .sortBy(noun => (noun || '').toLowerCase())
      .value();
  },

  allVisibleReadLabels(service: Service) {
    return ServiceUtils.allVisibleLabelsOfType('reads', service);
  },

  allVisibleWriteLabels(service: Service) {
    return ServiceUtils.allVisibleLabelsOfType('writes', service);
  },

  allVisibleSearchLabels(service: Service) {
    return ServiceUtils.allVisibleLabelsOfType('searches', service);
  },

  actionMeta,

  connectedServices,

  banner(service: { banner: string } | Service) {
    return _.get(service, 'banner', null);
  },

  /*
   * Finds the action associated with the passed in service and current action.
   * Useful for e.g. finding the 'search' action corresponding to a 'search_or_write' action, or vice-versa.
   */
  getAssociatedAction({
    service,
    currentAction,
    currentTypeOf,
    desiredTypeOf,
  }: {
    service: Service,
    currentAction: string,
    currentTypeOf: ServiceActionType,
    desiredTypeOf: ServiceActionType,
  }) {
    const searchOrWriteMeta = getSearchOrWriteMeta(
      service,
      currentAction,
      currentTypeOf
    );
    if (!searchOrWriteMeta) {
      return null;
    }

    const key = nodeTypeToDelegateMap[desiredTypeOf];
    if (!key) {
      return null;
    }

    return searchOrWriteMeta[key];
  },

  matchesQuery: (query: string, service: Object) => {
    return fuzzysearch(query, service[NAME]);
  },

  queryScore: (query: string, service: Object) => {
    return -fuzzyscore(query, service[NAME]);
  },

  search: memoize((query: string, services: Array<Service>) => {
    if (!query) {
      return services;
    }

    return services
      .filter(_.partial(ServiceUtils.matchesQuery, query))
      .sort((serviceA, serviceB) => {
        const compareQueryScore =
          ServiceUtils.queryScore(query, serviceA) -
          ServiceUtils.queryScore(query, serviceB);
        if (compareQueryScore) {
          return compareQueryScore;
        }

        return 0;
      });
  }),

  getCommonProblemsHelpLink: (service = {}) => {
    return service.slug
      ? `/apps/${service.slug}/help#common-problems-${service.slug}`
      : `/help/search?q=${encodeURIComponent(service.name)}`;
  },

  getVisibleServices,

  getAvailableServices,

  getServicesAvailableToNode({
    nodeSelectedApi,
    services,
    onlyPublic = false,
    showPathApi = true,
  }: {
    nodeSelectedApi?: string,
    services: ServicesMap | Service[],
    onlyPublic?: boolean,
    showPathApi: boolean,
  }) {
    const maybePublicCheck: (service: Service) => ?boolean = onlyPublic
      ? isPublic
      : _.stubTrue;
    // Hide deprecated services except for Nodes already using them.
    return _.filter(services, service => {
      return (
        _.get(service, 'id') === nodeSelectedApi ||
        (!isDeprecated(service) &&
          maybePublicCheck(service) &&
          (showPathApi || !isPathApi(service.key)))
      );
    });
  },

  getYourServices,

  getServicesByKey,

  getFeaturedServices(services: Service[], transform = s => s) {
    return transform(_.filter(services, ServiceUtils.isFeatured));
  },

  getRecommendedServices(services: Service[], transform = s => s, max = 8) {
    return _.take(transform(services), max);
  },

  getBuiltInServices(services: Service[], transform = s => s) {
    return transform(_.filter(services, ServiceUtils.isBuiltIn));
  },

  getAuthServices(services: ServicesMap): Service[] {
    return _.filter(
      services,
      service => !isHidden(service) && ServiceUtils.needsAuth(service)
    );
  },

  // This function will filter out any Service that does not contain an `id`.
  // According to the flow type for Service, it is possible to have an `id` that
  // is `null` or `undefined`.
  // https://github.com/zapier/zapier/blob/5540c1adc7c0de3f23456d0a3b656311b6d490cd/web/frontend/assets/app/service/types.js#L35
  getServicesById(services: Array<Service>): ServicesMap {
    return _.reduce(
      services,
      (acc, service) => {
        if (service && service.id) {
          acc[service.id] = service;
        }

        return acc;
      },
      {}
    );
  },

  getServiceByKey,

  getServiceByKeyWithOptionalVersion(
    services: ServicesMap | Service[],
    serviceKey: string
  ) {
    const withAnyVersion = `${stripVersion(serviceKey)}@`;
    const possibleServices = _.filter(
      services,
      ({ key }) =>
        key === serviceKey || (key || '').indexOf(withAnyVersion) === 0
    );
    const selectedService = possibleServices
      .sort((a, b) => {
        if (a.key === serviceKey) {
          return 1;
        }
        if (b.key === serviceKey) {
          return -1;
        }
        return compareVersions(getVersion(a.key), getVersion(b.key));
      })
      .reverse()[0];
    return selectedService;
  },

  getServiceBySlug(services: ServicesMap | Service[], serviceSlug: string) {
    return _.find(services, ({ slug }) => slug === serviceSlug);
  },

  cleanServiceClassname(key: string, size: string) {
    if (_.isEmpty(key)) {
      return key;
    }

    const className = hasVersion(key) ? stripVersion(key) : key;

    return className.includes(size) ? className : `${className}${size}`;
  },

  getServiceKeyWithoutVersion(service: Service = {}) {
    return stripVersion(service.key);
  },

  getServiceName(service: Service) {
    return _.trim((service[NAME] || '').replace('by Zapier', ''));
  },

  hasNoTriggersOrActions(service: Service): boolean {
    const types = [
      nodeTypes.HAS_READS,
      nodeTypes.HAS_WRITES,
      nodeTypes.HAS_SEARCHES,
      nodeTypes.HAS_FILTERS,
      nodeTypes.HAS_SEARCH_OR_WRITE,
    ];

    return types.every(type => !service[type]);
  },

  isTrigger(nodeType: ServiceActionType) {
    return nodeType === ServiceActionTypes.READ;
  },

  hasVisibleTriggers(service: Service) {
    return service[nodeTypes.HAS_READS] === true;
  },

  hasVisibleActions(service: Service): boolean {
    const types = [
      nodeTypes.HAS_WRITES,
      nodeTypes.HAS_SEARCHES,
      nodeTypes.HAS_FILTERS,
      nodeTypes.HAS_SEARCH_OR_WRITE,
    ];

    return types.some(type => service[type] === true);
  },

  isServiceDisabled(
    service: Service,
    {
      nodeType,
      isLastStep,
    }: { nodeType: ServiceActionType, isLastStep: boolean }
  ) {
    service = service || {};

    if (!isLastStep && isPathApi(service.key)) {
      return true;
    }

    // check for half-built public dev apps with no triggers/actions/searches/searchOrWrites:
    if (ServiceUtils.hasNoTriggersOrActions(service)) {
      return true;
    }

    if (ServiceUtils.isTrigger(nodeType)) {
      return !ServiceUtils.hasVisibleTriggers(service);
    }

    return !ServiceUtils.hasVisibleActions(service);
  },

  getPrimaryColor(service: Service) {
    if (isBuiltIn(service)) {
      return Colors.lava;
    }

    const primaryColor = _.get(service, ['primaryColor']);
    return primaryColor ? `#${primaryColor}` : Colors.heroine;
  },

  isBuiltIn,
  isDeprecated,
  isHidden,
  isFeatured,
  isPremium,
  isPublic,
  isBeta,
  isOauth,
  isInvite,
  hasUpfrontFields,
  excludePathsApi,
};

export default ServiceUtils;
